import React from "react";
import { ArrowRight } from "lucide-react";
import { Container, Eyebrow, CtaButton, useReveal } from "../components/primitives";
import { Photo } from "../components/Photo";

const NODES = [
  { id: "person", label: "Person", x: 10, y: 72 },
  { id: "skills", label: "Skills", x: 30, y: 38 },
  { id: "experience", label: "Experience", x: 50, y: 62 },
  { id: "match", label: "Match", x: 70, y: 30 },
  { id: "outcome", label: "Business Outcome", x: 90, y: 56 },
];

function SignalNetwork() {
  const pts = NODES.map((n) => `${n.x},${n.y}`).join(" ");
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full" aria-hidden="true">
      <defs>
        <linearGradient id="sig-line" x1="0" x2="1"><stop offset="0" stopColor="#FF8A3D" stopOpacity="0.9" /><stop offset="1" stopColor="#00D4FF" stopOpacity="0.9" /></linearGradient>
        <filter id="sig-glow"><feGaussianBlur stdDeviation="1.2" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
      </defs>
      <polyline points={pts} fill="none" stroke="rgba(255,138,61,0.25)" strokeWidth="0.6" vectorEffect="non-scaling-stroke" />
      <polyline points={pts} fill="none" stroke="url(#sig-line)" strokeWidth="1.4" vectorEffect="non-scaling-stroke" className="signal-path" filter="url(#sig-glow)" />
      {[[10, 72, 22, 88], [30, 38, 18, 20], [50, 62, 58, 84], [70, 30, 82, 14], [90, 56, 78, 78]].map(([x1, y1, x2, y2], i) => (
        <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgba(0,212,255,0.25)" strokeWidth="0.5" vectorEffect="non-scaling-stroke" />
      ))}
      {NODES.map((n, i) => (
        <g key={n.id} className="signal-node" style={{ animationDelay: `${i * 0.45}s` }}>
          <circle cx={n.x} cy={n.y} r="2.2" fill="#0A1628" stroke="#FF8A3D" strokeWidth="0.9" vectorEffect="non-scaling-stroke" />
          <circle cx={n.x} cy={n.y} r="0.9" fill="#FF8A3D" />
        </g>
      ))}
    </svg>
  );
}

export default function SignalSection() {
  const ref = useReveal();
  return (
    <section ref={ref} className="relative overflow-hidden bg-navy text-white" id="signal" data-testid="signal-section">
      <Photo name="softwarePair" fill parallax={12} treat="hero" particles priority={false} sizes="100vw" />
      <Container className="relative py-24 lg:py-36 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 reveal">
          <Eyebrow dark>Human + technology</Eyebrow>
          <h2 className="mt-5 font-display text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.02]">
            Technology finds the signal.<br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF8A3D] to-[#00D4FF]">Humans understand the fit.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-white/70 leading-relaxed max-w-xl">Our intelligence layer connects a person's skills and experience to the match — and the match to a business outcome. Our people make the judgment calls the data cannot.</p>
          <div className="mt-9 flex flex-wrap gap-3">
            <CtaButton to="/contact?form=employer" testId="signal-employer-cta">Request Staffing <ArrowRight className="ml-2 h-4 w-4" /></CtaButton>
            <CtaButton to="/services" variant="outline" testId="signal-services-cta">How we work</CtaButton>
          </div>
        </div>
        <div className="lg:col-span-6 reveal">
          <div className="relative glass-dark rounded-3xl p-6 lg:p-8 min-h-[300px] lg:min-h-[380px]" data-testid="signal-network">
            <SignalNetwork />
            <div className="relative grid grid-cols-5 gap-2 text-center h-full items-end pt-40 lg:pt-56">
              {NODES.map((n, i) => (
                <div key={n.id} className="flex flex-col items-center gap-2">
                  <span className="eyebrow text-[#FF8A3D]">0{i + 1}</span>
                  <span className="text-[11px] sm:text-xs font-semibold text-white/85 leading-tight">{n.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}
